'use client';

import {
    useConsumerSubscriptionPlan,
    useConsumerSubscriptionValuePlan,
    ConsumerSubscriptionPlan,
    ConsumerSubscriptionValuePlan,
    SubscriptionPlanContext,
} from '@udemy-v2/subscription-graphql';

/**
 * Type guard to check if a plan context has valid pricing data.
 * Matches v1's hasValidPricing helper.
 */
const hasValidPricing = (
    context: SubscriptionPlanContext<
        ConsumerSubscriptionPlan | ConsumerSubscriptionValuePlan
    > | null,
): context is ConsumerSubscriptionPlan | ConsumerSubscriptionValuePlan => {
    return Boolean(
        context &&
            !context.isLoading &&
            'annualPlanOption' in context &&
            context.annualPlanOption?.monthlyPrice?.amount,
    );
};

/**
 * Subscription plans hook for unauthenticated LOHP.
 * Returns both Value Plan and Personal Plan contexts directly from the package.
 *
 * Tri-state return per plan:
 *   {isLoading: true} → loading
 *   null              → not available
 *   plan object       → available
 */
export function useSubscriptionPlans() {
    const valuePlan = useConsumerSubscriptionValuePlan();
    const personalPlan = useConsumerSubscriptionPlan();

    // Check if at least one plan has resolved
    const isValuePlanResolved = valuePlan !== undefined && valuePlan?.isLoading === false;
    const isPersonalPlanResolved = personalPlan !== undefined && personalPlan?.isLoading === false;
    const hasAtLeastOneResolved = isValuePlanResolved || isPersonalPlanResolved;
    const isLoading = !hasAtLeastOneResolved;

    // Select best valid plan (prioritize Value Plan)
    const hasValuePlan = hasValidPricing(valuePlan);
    const selectedPlan = hasValuePlan ? valuePlan : personalPlan;
    const plan = hasValidPricing(selectedPlan) ? selectedPlan : null;

    return {
        isLoading,
        hasValuePlan,
        plan,
    };
}
